import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CustomTable from "../CustomTable";
import Capabilities from "../Capabilities";
import ScopeWithAdditionalTags from "../ScopeWithAdditionalTags";
import StatusIndicator from "../StatusIndicator";
import RequestTypeBadge from "./RequestTypeBadge";
import AdditionalParticipatingSponsorsCell from "./AdditionalParticipatingSponsorsCell";
import {
  buildSponsorScopedDetailsUrl,
  requestTypeToStatusParam,
} from "../../utils/sponsorScopedDetailsUrl";
import {
  getAssessmentRequestArchiveCommentDisplay,
  getAssessmentRequestAuditorDisplay,
  getAssessmentRequestGxpDisplay,
  getAssessmentRequestLocationDisplay,
  getAssessmentRequestMultiSponsorDisplay,
  getAssessmentRequestOnsiteRemoteDisplay,
  getAssessmentRequestTypeDisplay,
  formatAssessmentHistoryShortDate,
} from "../../utils/assessmentRequestGridFields";
import { getVqaListStatusForIndicator } from "../../utils/vqaListItemNormalizer";
import AssessmentRequestGridActionMenu from "./AssessmentRequestGridActionMenu";
import {
  COMPACT_GRID_COL_WIDTH_PCT,
  compactGridDateCellClassName,
  compactGridMultiSponsorCellClassName,
  compactGridOnsiteRemoteCellClassName,
  compactGridStatusCellClassName,
  SIZE_TABLE_COL_MIN,
  SIZE_TABLE_TEXT_COL_MAX,
} from "../RFIs/rfiGridTableStyles";
import { ASSESSMENT_FIELD_LABELS } from "../../utils/assessmentAdminFieldConfig";

export function getAssessmentRequestMultiSponsorColumnHeader() {
  return ASSESSMENT_FIELD_LABELS.multiSponsorGrid;
}

function sponsorDetailsUrl(item) {
  return buildSponsorScopedDetailsUrl(
    `/Backend/Assessment/${item?.vqa_request_id}/Manage`,
    item?.sponsor_name,
    requestTypeToStatusParam(item?.request_type)
  );
}

function EmptyOrText({ value }) {
  if (value == null || String(value).trim() === "") {
    return <span className="text-gray-400">--</span>;
  }
  return <span className="break-words text-gray-700">{value}</span>;
}

/**
 * Cell padding for Assessment Request grids (Innovation Lab pattern).
 */
export function getAssessmentRequestGridCellPaddingClass(colIndex, totalCols, isHeader) {
  const py = isHeader ? "py-5" : "py-4";
  if (totalCols <= 1) {
    return `${py} pl-4 pr-4 sm:pl-6 sm:pr-6`;
  }
  const isFirst = colIndex === 0;
  const isLast = colIndex === totalCols - 1;
  if (isFirst) return `${py} pl-4 pr-2 sm:pl-6`;
  if (isLast) return `${py} pl-3 pr-4 sm:pr-6`;
  return `${py} px-2`;
}

/**
 * Shared assessment request table: Innovation Lab styling.
 *
 * @param {'admin'|'sponsor'|'provider'} [props.actionVariant='admin'] — Routes "View Details" to admin Manage vs sponsor/provider Details.
 * @param {boolean} [props.showSponsorColumn=true]
 * @param {boolean} [props.showVendorColumn=true]
 * @param {boolean} [props.collapseReportStatuses=false] — Map anonymized/published to Final Report Delivered for the status badge (legacy sponsor UX).
 */
export default function BackendAssessmentRequestGridTable({
  data,
  sortConfig,
  onSort,
  // Kept for API compatibility with parent pages (admin list passes refetch / mutations).
  refetch = () => {},
  // Kept for API compatibility with parent pages (admin); actions column uses View Details only.
  approve: _approve,
  reject: _reject,
  approveError: _approveError,
  rejectError: _rejectError,
  approveIsError: _approveIsError,
  rejectIsError: _rejectIsError,
  setStatus: _setStatus,
  isSearchActive = false,
  emptyMessage = "No documents found",
  emptyDescription = "There are no assessment requests available at the moment.",
  rowKey = "vqa_request_id",
  showSponsorColumn = true,
  showVendorColumn = true,
  actionVariant = "admin",
  collapseReportStatuses = false,
}) {
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const sponsorCol = {
      key: "sponsorName",
      header: "Sponsor",
      sortable: true,
      width: "8%",
      minWidth: SIZE_TABLE_COL_MIN.org,
      contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
      cellClassName: "text-gray-900 font-medium",
      render: (item) =>
        actionVariant === "admin" ? (
          <button
            type="button"
            className="text-left hover:cursor-pointer hover:underline break-words"
            onClick={(e) => {
              e.stopPropagation();
              navigate(sponsorDetailsUrl(item));
            }}
          >
            {item?.sponsor_name}
          </button>
        ) : (
          <span className="break-words">{item?.sponsor_name}</span>
        ),
    };

    const vendorCol = {
      key: "vendor",
      header: "Vendor",
      sortable: true,
      width: "8%",
      minWidth: SIZE_TABLE_COL_MIN.org,
      contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
      cellClassName: "text-gray-900 font-medium",
      render: (item) =>
        actionVariant === "sponsor" ? (
          <button
            type="button"
            className="text-left hover:cursor-pointer hover:underline break-words"
            onClick={() =>
              navigate(`/Assessment/${item?.vqa_request_id}/Details`)
            }
          >
            {item?.provider_name}
          </button>
        ) : (
          <span className="break-words">{item?.provider_name}</span>
        ),
    };

    const baseCols = [
      ...(showSponsorColumn ? [sponsorCol] : []),
      ...(showVendorColumn ? [vendorCol] : []),
      // TODO: uncomment to display gxp and type columns
      // {
      //   key: "assessmentType",
      //   header: "Type",
      //   sortable: true,
      //   width: "7%",
      //   cellClassName: "text-gray-900",
      //   render: (item) => (
      //     <EmptyOrText value={getAssessmentRequestTypeDisplay(item)} />
      //   ),
      // },
      // {
      //   key: "gxp",
      //   header: "GxP",
      //   sortable: true,
      //   width: "6%",
      //   cellClassName: "text-gray-900",
      //   render: (item) => (
      //     <EmptyOrText value={getAssessmentRequestGxpDisplay(item)} />
      //   ),
      // },
      {
        key: "scope",
        header: "Scope",
        sortable: false,
        width: "12%",
        minWidth: SIZE_TABLE_COL_MIN.scope,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        render: (item) => (
          <ScopeWithAdditionalTags
            services={item.capabilities || item.Capabilities}
            additionalScope={item.additional_scope}
            addedScope={item.added_scope}
            addedScopeTags={item.addon_scope_tags}
            capabilitiesProps={{
              icon: true,
              showLimited: false,
              badgesPerRow: 3,
            }}
          />
        ),
      },
      {
        key: "location",
        header: "Location",
        sortable: true,
        width: "8%",
        minWidth: SIZE_TABLE_COL_MIN.location,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900",
        render: (item) => (
          <EmptyOrText value={getAssessmentRequestLocationDisplay(item)} />
        ),
      },
      {
        key: "onsiteRemote",
        header: "Onsite / Remote",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.onsiteRemote,
        cellClassName: compactGridOnsiteRemoteCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => (
          <EmptyOrText value={getAssessmentRequestOnsiteRemoteDisplay(item)} />
        ),
      },
      {
        key: "multiSponsor",
        header: getAssessmentRequestMultiSponsorColumnHeader(),
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.multiSponsor,
        cellClassName: compactGridMultiSponsorCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => (
          <span className="text-gray-700">
            {getAssessmentRequestMultiSponsorDisplay(item)}
          </span>
        ),
      },
      ...(actionVariant === "admin"
        ? [
            {
              key: "requestType",
              header: "Request Type",
              sortable: true,
              width: COMPACT_GRID_COL_WIDTH_PCT,
              minWidth: SIZE_TABLE_COL_MIN.requestType,
              headerClassName: "text-center [&>div]:justify-center",
              cellClassName: "text-center",
              render: (item) => (
                <RequestTypeBadge requestType={item?.request_type} />
              ),
            },
            {
              key: "additionalParticipatingSponsors",
              header: "Additional Participating Sponsors",
              sortable: false,
              width: "10%",
              minWidth: SIZE_TABLE_COL_MIN.org,
              contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
              render: (item) => (
                <AdditionalParticipatingSponsorsCell
                  sponsors={item?.additional_participating_sponsors}
                  ownSponsorName={item?.sponsor_name}
                  vqaRequestId={item?.vqa_request_id}
                  navigate={navigate}
                />
              ),
            },
          ]
        : []),
      {
        key: "auditor",
        header: "Auditor",
        sortable: true,
        width: "8%",
        minWidth: SIZE_TABLE_COL_MIN.auditor,
        contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
        cellClassName: "text-gray-900",
        render: (item) => {
          const name = getAssessmentRequestAuditorDisplay(item);
          return name ? (
            <span className="break-words">{name}</span>
          ) : (
            <span className="text-gray-400">--</span>
          );
        },
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.status,
        cellClassName: compactGridStatusCellClassName,
        render: (item) => (
          <div className="text-gray-500 min-w-0">
            <StatusIndicator
              status={getVqaListStatusForIndicator(item, {
                collapseReportStatuses,
              })}
            />
          </div>
        ),
      },
      {
        key: "requestDate",
        header: "Request Date",
        sortable: true,
        width: COMPACT_GRID_COL_WIDTH_PCT,
        minWidth: SIZE_TABLE_COL_MIN.date,
        cellClassName: compactGridDateCellClassName,
        headerClassName: "text-center [&>div]:justify-center",
        render: (item) => {
          const d = formatAssessmentHistoryShortDate(
            item?.vqa_request_created_On
          );
          return d ? (
            <span className="text-gray-700">{d}</span>
          ) : (
            <span className="text-gray-400">--</span>
          );
        },
      },
      ...(actionVariant === "admin"
        ? [
            {
              key: "assessmentDate",
              header: "Assessment Date",
              sortable: true,
              width: COMPACT_GRID_COL_WIDTH_PCT,
              minWidth: SIZE_TABLE_COL_MIN.date,
              cellClassName: compactGridDateCellClassName,
              headerClassName: "text-center [&>div]:justify-center",
              render: (item) => {
                const d = formatAssessmentHistoryShortDate(
                  item?.assessment_date
                );
                return d ? (
                  <span className="text-gray-700">{d}</span>
                ) : (
                  <span className="text-gray-400">--</span>
                );
              },
            },
          ]
        : []),
      // {
      //   key: "releasedDate",
      //   header: "Release Date",
      //   sortable: true,
      //   width: "7%",
      //   cellClassName: "text-center",
      //   headerClassName: "text-center [&>div]:justify-center",
      //   render: (item) => {
      //     const d = item?.released_date
      //       ? formatAssessmentHistoryShortDate(item.released_date)
      //       : "";
      //     return d ? (
      //       <span className="text-gray-700">{d}</span>
      //     ) : (
      //       <span className="text-gray-400">--</span>
      //     );
      //   },
      // },
    ];

    // Admin-only: matches Library Requests / Innovation Lab archive comment column pattern.
    const archiveCommentCol =
      actionVariant === "admin"
        ? {
            key: "archiveComment",
            header: "Archive Comment",
            sortable: false,
            width: "10%",
            minWidth: SIZE_TABLE_COL_MIN.document,
            contentMaxWidth: SIZE_TABLE_TEXT_COL_MAX,
            cellClassName: "text-gray-500",
            render: (item) => {
              const text = getAssessmentRequestArchiveCommentDisplay(item);
              return text ? (
                <span
                  className="italic text-xs line-clamp-2"
                  title={text}
                >
                  {text}
                </span>
              ) : (
                <span className="text-gray-300">--</span>
              );
            },
          }
        : null;

    const actionsCol = {
      key: "actions",
      header: "Actions",
      sortable: false,
      minWidth: SIZE_TABLE_COL_MIN.actions,
      align: "right",
      cellClassName: "text-right",
      excludeFromRowClassName: true,
      render: (item) => (
        <div
          className="flex justify-end shadow-ring"
          onClick={(e) => e.stopPropagation()}
        >
          <AssessmentRequestGridActionMenu
            item={item}
            actionVariant={actionVariant}
            refetch={refetch}
          />
        </div>
      ),
    };

    // Hide actions column for provider; archive comment column is admin-only.
    return [
      ...baseCols,
      archiveCommentCol,
      actionVariant !== "provider" ? actionsCol : undefined,
    ].filter((col) => col != null);
  }, [
    actionVariant,
    collapseReportStatuses,
    navigate,
    showSponsorColumn,
    showVendorColumn,
    refetch,
  ]);

  return (
    <div className="w-full min-w-0 max-w-full">
      <CustomTable
        columns={columns}
        data={data}
        sortConfig={sortConfig}
        onSort={onSort}
        fixedColumnWidths
        emptyMessage={emptyMessage}
        emptyDescription={emptyDescription}
        isSearchActive={isSearchActive}
        edgeToEdge
        className="py-0 sm:py-0"
        rowKey={rowKey}
        onRowClick={
          actionVariant === "admin"
            ? (item) => navigate(sponsorDetailsUrl(item))
            : undefined
        }
        getHeaderCellPaddingClass={(colIndex, totalCols) =>
          getAssessmentRequestGridCellPaddingClass(colIndex, totalCols, true)
        }
        getBodyCellPaddingClass={(colIndex, totalCols) =>
          getAssessmentRequestGridCellPaddingClass(colIndex, totalCols, false)
        }
        scrollWrapperClassName="bg-white"
        tableDivideClassName="divide-gray-200"
        theadClassName="bg-gray-50 [&_th]:align-middle [&_th]:min-h-[3rem]"
        tbodyClassName="divide-y divide-gray-200 bg-white"
        useDividedBodyRows
        getRowClassName={(item) => {
          const s =
            item?.vqa_request_status ?? item?.vqa_status ?? "";
          return s === "Archived" ? "opacity-60" : "";
        }}
      />
    </div>
  );
}
